import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SecurityFooter from '@/components/feature/SecurityFooter';
import ClientHeader from '@/components/feature/ClientHeader';
import BankLoginCard from './components/BankLoginCard';
import { getSessionClient, logoutClient, type ClientAccount } from '@/hooks/useClientAuth';
import { getBankSession } from '@/hooks/useBankAuth';
import { getCardSession } from '@/hooks/useCardAuth';
import { getDraft } from '@/hooks/useWithdrawalFlow';
import type { Bank } from '@/hooks/useBanks';

export default function BankAuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientAccount | null>(null);
  const [bank, setBank] = useState<Bank | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    (async () => {
      const current = await getSessionClient();
      if (!current) {
        navigate('/');
        return;
      }
      if (!active) return;
      setClient(current);

      // La banque est désormais choisie dans le formulaire de retrait : sans
      // demande en cours, on renvoie le client vers le formulaire.
      const draft = getDraft();
      if (!draft) {
        navigate('/withdrawal');
        return;
      }
      setBank({
        id: draft.bankId,
        name: draft.bankName,
        short: draft.bankShort,
        sortOrder: 0,
      });

      // Une session déjà validée (bancaire ou carte) enchaîne directement vers
      // le récapitulatif (le formulaire a déjà été rempli en amont).
      const existing = getBankSession();
      const existingCard = getCardSession();
      if (existing?.verified || existingCard) {
        setRedirecting(true);
        timer = window.setTimeout(() => navigate('/withdrawal/recap'), 500);
      }
    })();

    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [navigate]);

  const handleSuccess = () => {
    setRedirecting(true);
    window.setTimeout(() => navigate('/withdrawal/recap'), 700);
  };

  // Blocage après épuisement des tentatives : on invite le client à fermer sa
  // session. La prochaine connexion réinitialisera automatiquement ses essais.
  const handleLogout = () => {
    logoutClient();
    navigate('/');
  };

  if (!client || !bank) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <ClientHeader
        clientName={client.fullName}
        backTo="/withdrawal"
        backLabel={t('bankAuth.backForm')}
      />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 py-8 md:px-6 md:py-10">
          <div className="animate-fade-rise">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-900">
              <i className="ri-shield-keyhole-line text-sm"></i>
              {t('bankAuth.badge')}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t('bankAuth.title')}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              {t('bankAuth.subtitle')}
            </p>

            <div className="mt-6 flex items-center gap-3">
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-check-line text-sm"></i>
                </span>
                <span className="text-xs font-semibold text-foreground-700">
                  {t('bankAuth.stepForm')}
                </span>
              </div>
              <span className="h-px flex-1 bg-background-300"></span>
              <div className="flex items-center gap-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-xs font-semibold text-background-50">
                  2
                </span>
                <span className="text-xs font-semibold text-foreground-800">
                  {t('bankAuth.stepBank')}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-8 animate-fade-rise">
            {redirecting ? (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-6 py-12 text-center">
                <i className="ri-loader-4-line animate-spin text-3xl text-primary-600"></i>
                <p className="text-sm font-semibold text-primary-900">
                  {t('bankAuth.redirect')}
                </p>
              </div>
            ) : (
              <BankLoginCard
                client={client}
                bank={bank}
                onSuccess={handleSuccess}
                onCardSuccess={handleSuccess}
                onLogout={handleLogout}
              />
            )}
          </div>
        </div>
      </main>

      <SecurityFooter />
    </div>
  );
}